import React from 'react';
import { GovAccordion } from '@gov-design-system-ce/react';
import { useTranslationWithNamespace } from '@gov-nx/core/service';
import type { PageCode } from '@gov-nx/module/page';
import { useEPeticeDetailContext } from '@gov-nx/module/page';
import { AccordionItem, CommaSpace, LayoutGap, Space } from '@gov-nx/ui/web';
import { toDateReadable } from '@gov-nx/utils/common';

export interface EPetitionInfoProps {
	code: PageCode;
}

export const EPetitionInfo = ({ code }: EPetitionInfoProps) => {
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const ts = getLocalizeCurried(code);
	const { petition, members } = useEPeticeDetailContext();

	return (
		<GovAccordion
			size="m"
			noBorder={true}
			aria-labelledby="informace-k-epetici">
			<AccordionItem
				label={<h3 id="informace-k-epetici">{ts('data.petice-informace')}</h3>}>
				<LayoutGap
					gap="m"
					className="pb-(--templates-margin-m)">
					<h4 className="mt-(--templates-margin-m)!">
						{ts('data.zakladni-udaje')}
					</h4>
					<dl>
						<div>
							<dt className="inline mr-1! font-bold text-(--text-secondary)">
								{ts('data.datum-zverejneni')}
							</dt>
							<dd className="inline">
								{petition?.datumZverejneni &&
									toDateReadable(petition?.datumZverejneni)}
							</dd>
						</div>
						<div>
							<dt className="inline mr-1! font-bold text-(--text-secondary)">
								{ts('data.ukonceni-petice')}
							</dt>
							<dd className="inline">
								{petition?.datumUkonceni &&
									toDateReadable(petition?.datumUkonceni)}
								<Space />
								<i>{ts('data.ukonceni-petice-poznamka')}</i>
							</dd>
						</div>
						<div>
							<dt className="inline mr-1! font-bold text-(--text-secondary)">
								{ts('data.id-petice')}
							</dt>
							<dd className="inline">{petition?.id}</dd>
						</div>
					</dl>
					{petition?.zakladatel && (
						<>
							<h4 className="mt-(--templates-margin-m)!">
								{ts('data.zakladatel')}
							</h4>
							{petition?.zakladatel?.pravnickaOsoba ? (
								<div>
									<p>
										<strong>
											{petition?.zakladatel?.pravnickaOsoba?.nazev}
										</strong>
										<br />
										{petition?.zakladatel?.pravnickaOsoba?.adresa?.adresaText}
									</p>
									<dl>
										<div>
											<dt className="inline mr-1!">{ts('data.ico')}</dt>
											<dd className="inline">
												{petition?.zakladatel?.pravnickaOsoba?.ico}
											</dd>
										</div>
										<div>
											<dt className="inline mr-1!">
												{ts('data.datova-schranka')}
											</dt>
											<dd className="inline">
												{petition?.zakladatel?.pravnickaOsoba?.datovaSchrankaId}
											</dd>
										</div>
									</dl>
								</div>
							) : petition?.zakladatel?.fyzickaOsoba ? (
								<p>
									<strong>
										{petition?.zakladatel?.fyzickaOsoba?.jmeno}{' '}
										{petition?.zakladatel?.fyzickaOsoba?.prijmeni}
									</strong>
									<br />
									{petition?.zakladatel?.fyzickaOsoba?.adresa?.adresaText}
								</p>
							) : null}
							{members.length > 0 && (
								<div>
									<strong>{ts('data.peticni-vybor')}</strong>
									<dl>
										{members.map((member, index) => (
											<dd
												// eslint-disable-next-line react/no-array-index-key
												key={index}
												className="inline">
												{member.jmeno}
												<Space />
												{member.prijmeni}
												<CommaSpace />
												{member.adresa?.adresaText}
											</dd>
										))}
									</dl>
								</div>
							)}
							{petition?.adresat && (
								<>
									<h4 className="mt-(--templates-margin-m)!">
										{ts('data.adresat')}
									</h4>
									<div>
										<strong>{petition?.adresat?.nazev}</strong>
										<div>
											<span className="inline mr-1!">{ts('data.adresa')}</span>
											<span className="inline">
												{petition?.adresat?.adresa?.adresaText}
											</span>
										</div>
										<div>
											<span className="inline mr-1!">
												{ts('data.datova-schranka')}
											</span>
											<span className="inline">
												{petition?.adresat?.datovaSchrankaId}
											</span>
										</div>
									</div>
								</>
							)}
						</>
					)}
				</LayoutGap>
			</AccordionItem>
		</GovAccordion>
	);
};
